import { BasketDrawerVersion } from '@common/components/BasketDrawer';
import {
  CloseModalButton,
  FormWrapper,
  Header,
  InnerScroller,
  LeftSideBarWrapper,
  MobileHeading,
  Modal,
  PrevStepButton,
  RightSideBarWrapper,
  SplitBarWrapper,
  SubTitle,
  Title,
} from '@common/components/BookingForms';
import { Section } from '@common/components/BookingForms/Section';
import { Spinner } from '@common/components/Spinner';
import { StickyBarWrapper } from '@common/components/StickyBar';
import { Routes } from '@common/constants';
import { useRoomExtras } from '@features/rooms';
import { useGetSpaSettings } from '@features/spa';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from '@lang';
import { some } from '@utils/some';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useRouter } from 'shared-routing';
import { RoomExtra } from 'shared-types/Room';
import { SignIn } from '../SignIn';
import { RoomRateFormStepNames, STICKY_BAR_ID, getFormSteps } from './FormSteps';
import { ExtrasStepV2 } from './FormSteps/ExtrasStepV2';
import { GuestDetailsStepV2 } from './FormSteps/GuestDetailsStepV2';
import { buildRoomRateFormSchema, RoomRateFormValues } from './RoomRateForm.schema';
import { RoomRateConfigurationWizardProps } from './RoomRateForm.types';

const titleIds = {
  [RoomRateFormStepNames.GUESTS]: 'room.guestDetailsStepTitle',
  [RoomRateFormStepNames.EXTRAS]: 'room.extrasStepTitle',
};

const routesMap = {
  room: {
    booking: Routes.ROOM_BOOKING,
    detail: Routes.ROOM_DETAIL,
  },
  rate: {
    booking: Routes.RATE_BOOKING,
    detail: Routes.RATE_DETAIL,
  },
};

export const RoomRateConfigurationWizard: FC<RoomRateConfigurationWizardProps> = ({
  bookingStep,
  property,
  basketDetails,
  isFetching,
  basketGuests,
  journeyParams,
  roomRateAddToBasket,
  multiRoomRateAddToBasket,
  language,
  metaEntity,
  entity,
  loggedGuest,
}) => {
  const router = useRouter();
  const { t } = useTranslations();

  const totalGuestAmount = journeyParams.adults + journeyParams.children;
  const isMultiRoom = some(journeyParams.rooms) && journeyParams.rooms.length > 1;
  const isEcom2 = useFeatureIsOn('feature.ecom2');
  const isV2Flow = isEcom2 && !isMultiRoom;
  const isV2GuestStep = isV2Flow && bookingStep === RoomRateFormStepNames.GUESTS;
  const isV2ExtrasStep = isV2Flow && bookingStep === RoomRateFormStepNames.EXTRAS;
  const isPortallingStep =
    bookingStep === RoomRateFormStepNames.GUESTS || bookingStep === RoomRateFormStepNames.EXTRAS;
  const usesStickyBar = !isV2Flow && isPortallingStep;
  const addToBasket = isMultiRoom
    ? multiRoomRateAddToBasket({
        basketDetails,
        journeyParams,
        property,
        language,
        entity,
        metaEntity,
      })
    : roomRateAddToBasket({
        basketDetails,
        journeyParams,
        property,
        language,
        entity,
        metaEntity,
      });

  const buildDefaultGuests = () => {
    if (isMultiRoom) {
      return (journeyParams.rooms ?? []).flatMap((room: any) =>
        Array.from({ length: room.adults }, () => ({
          room: room.roomNumber,
          title: '',
          firstName: '',
          lastName: '',
          gender: '',
        }))
      );
    }
    return Array.from({ length: totalGuestAmount }, () => ({
      title: '',
      firstName: '',
      lastName: '',
      gender: '',
    }));
  };

  const form = useForm<RoomRateFormValues>({
    resolver: zodResolver(buildRoomRateFormSchema(t)),
    mode: 'onChange',
    defaultValues: {
      isMultiRoom,
      guests: buildDefaultGuests(),
      note: '',
      extras: isMultiRoom ? {} : [],
    },
  });

  const roomsArr = Array.isArray(journeyParams.rooms) ? journeyParams.rooms : [];
  const roomsSignature = roomsArr
    .map((r: any) => `${r.roomNumber}:${r.adults}:${r.children}`)
    .join(',');

  useEffect(() => {
    form.reset({
      isMultiRoom,
      guests: buildDefaultGuests(),
      note: '',
      extras: isMultiRoom ? {} : [],
    });
  }, [journeyParams.adults, journeyParams.children, roomsSignature, isMultiRoom]);

  useEffect(() => {
    if (window && router) {
      router.beforePopState(({ as }) => {
        if (as.includes('/upgrade/')) {
          router.back();
          return false;
        }
        return true;
      });
    }
    return () => router.beforePopState();
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [, setIsSignInModalOpen] = useState(false);
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  const openModal = () => setIsModalOpen(true);

  const closeModal = () => {
    if (isModalOpen) {
      setIsModalOpen(false);
      form.reset();
    }
  };

  useEffect(() => {
    if (
      bookingStep &&
      Object.values(RoomRateFormStepNames).includes(bookingStep as RoomRateFormStepNames)
    ) {
      openModal();
    } else {
      closeModal();
    }
  }, [bookingStep]);

  const showConfirmation = () => setIsConfirmationVisible(true);

  const hideConfirmation = () => {
    router.push({
      pathname: routesMap[metaEntity.type].detail,
      query: { id: metaEntity.id },
    });
    setIsConfirmationVisible(false);
  };

  const goToNextStep = async () => {
    if (bookingStep === RoomRateFormStepNames.GUESTS && availableExtras?.length) {
      router.push({
        pathname: routesMap[metaEntity.type].booking,
        query: {
          ...router.query,
          id: metaEntity.id,
          bookingStep: RoomRateFormStepNames.EXTRAS,
          [entity.type]: entity.id,
        },
      });
    } else {
      showConfirmation();

      if (!isV2Flow) {
        closeModal();
      }
    }
  };

  const goToPreviousStep = () => {
    if (isV2Flow) {
      if (bookingStep === RoomRateFormStepNames.EXTRAS) {
        router.push({
          pathname: routesMap[metaEntity.type].booking,
          query: {
            ...router.query,
            id: metaEntity.id,
            bookingStep: RoomRateFormStepNames.GUESTS,
            [entity.type]: entity.id,
          },
        });
        return;
      }
      closeModal();
      router.push({
        pathname: routesMap[metaEntity.type].detail,
        query: { id: metaEntity.id },
      });
      return;
    }

    if (bookingStep === RoomRateFormStepNames.GUESTS) {
      closeModal();
    }
    router.back();
  };

  const roomId = String(metaEntity.type === 'room' ? metaEntity.id : entity.id);
  const rateId = String(metaEntity.type === 'room' ? entity.id : metaEntity.id);
  const fromDate = journeyParams.fromDate.format('YYYY-MM-DD');
  const toDate = journeyParams.toDate
    ? journeyParams.toDate.format('YYYY-MM-DD')
    : journeyParams.fromDate.clone().add(1, 'days').format('YYYY-MM-DD');

  const {
    data: extrasData,
    isLoading: isLoadingExtras,
    isPreviousData: isPreviousExtras,
  } = useRoomExtras({
    ...journeyParams,
    fromDate,
    toDate,
    roomId,
    rateId,
    language,
  });
  const areExtrasSettled = !isLoadingExtras && !isPreviousExtras;
  const availableExtras = areExtrasSettled ? extrasData ?? [] : null;
  const availableRoomExtras = availableExtras as RoomExtra[] | null;

  const formSteps = getFormSteps({
    basketGuests,
    journeyParams,
    addToBasket: addToBasket as any,
    setIsSignInModalOpen,
    goToNextStep,
    metaEntity: { ...metaEntity, id: String(metaEntity.id) },
    entity,
    totalGuestAmount,
    availableExtras: availableRoomExtras,
    loggedGuest,
  });

  const showBasketDrawer = isConfirmationVisible && basketDetails;
  const modules = Object.keys(property.modulesExcludingDisabled);

  return (
    <FormProvider {...form}>
      <div>
        {showBasketDrawer && (
          <BasketDrawerVersion
            open={isConfirmationVisible}
            onClose={hideConfirmation}
            propertyModules={modules}
            hideArrivalTimes={hideArrivalTimes}
          />
        )}
        {isV2GuestStep || isV2ExtrasStep ? (
          isModalOpen &&
          (isV2GuestStep ? (
            <GuestDetailsStepV2
              metaEntity={{ ...metaEntity, id: String(metaEntity.id) }}
              entity={entity}
              basketGuests={basketGuests}
              loggedGuest={loggedGuest}
              availableExtras={availableRoomExtras}
              addToBasket={addToBasket}
              goToNextStep={goToNextStep}
              goToPreviousStep={goToPreviousStep}
              isFetching={isFetching}
              maxGuests={totalGuestAmount || 1}
              language={language}
            />
          ) : (
            <ExtrasStepV2
              metaEntity={{ ...metaEntity, id: String(metaEntity.id) }}
              entity={entity}
              availableExtras={availableExtras}
              addToBasket={addToBasket}
              goToNextStep={goToNextStep}
              goToPreviousStep={goToPreviousStep}
              isFetching={isFetching}
              language={language}
            />
          ))
        ) : (
          <Modal isVisible={isModalOpen}>
            <>
              <Spinner showDelayMessage isActive={isFetching} />
              <Header>
                <PrevStepButton onClick={goToPreviousStep} />
                <MobileHeading>{metaEntity.title}</MobileHeading>
                <CloseModalButton
                  onClick={() =>
                    router.push({
                      pathname: routesMap[metaEntity.type].detail,
                      query: { id: metaEntity.id },
                    })
                  }
                />
              </Header>
              <InnerScroller scrollToTopIndicator={bookingStep}>
                <FormWrapper>
                  <Title titleId={titleIds[bookingStep || RoomRateFormStepNames.GUESTS]} />
                  <Section>
                    <SubTitle
                      titleId="roomRates.guestsSubtitle"
                      values={{ guestAmount: totalGuestAmount }}
                    />
                  </Section>
                  <SplitBarWrapper>
                    <LeftSideBarWrapper>{formSteps[bookingStep || '']}</LeftSideBarWrapper>
                    <RightSideBarWrapper>
                      <SignIn />
                    </RightSideBarWrapper>
                  </SplitBarWrapper>
                </FormWrapper>
              </InnerScroller>
            </>
          </Modal>
        )}
        {usesStickyBar && (
          <StickyBarWrapper id={STICKY_BAR_ID} zIndex={isModalOpen ? 9999 : undefined} />
        )}
      </div>
    </FormProvider>
  );
};
